
import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Line, Point, lineSegmentsIntersect } from "../utils/geometry";

const EPS = 1e-6;

export interface FixedRoomGeometry {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface FixedRoomDrawable extends FixedRoomGeometry {
    id: string;
    name?: string;
}

interface CanvasLogicOptions {
    fixedRoomMode?: boolean;
    fixedRooms?: FixedRoomDrawable[];
    pendingFixedRoom?: FixedRoomGeometry | null;
    onFixedRoomDrawn?: (rect: FixedRoomGeometry) => void;
}

const normalizeRect = (x1: number, y1: number, x2: number, y2: number): FixedRoomGeometry => ({
    x: Math.min(x1, x2),
    y: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
});

export const useCanvasLogic = (baseDist: number = 2, options: CanvasLogicOptions = {}) => {
    const {
        fixedRoomMode = false,
        fixedRooms = [],
        pendingFixedRoom = null,
        onFixedRoomDrawn,
    } = options;

    const svgRef = useRef<SVGSVGElement | null>(null);
    const gGridRef = useRef<SVGGElement | null>(null);
    const gLinesRef = useRef<SVGGElement | null>(null);
    const gFixedRoomsRef = useRef<SVGGElement | null>(null);
    const gPointsRef = useRef<SVGGElement | null>(null);

    const gDimensionsRef = useRef<SVGGElement | null>(null);
    const gDeleteButtonsRef = useRef<SVGGElement | null>(null);

    const [zoom, setZoom] = useState(1);
    const [drawing, setDrawing] = useState(true);
    const [deleteWallMode, setDeleteWallMode] = useState(false);
    const [spacing, setSpacing] = useState(baseDist);
    const [revision, setRevision] = useState(0);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const baseDistRef = useRef(baseDist);
    const visualSpacingRef = useRef(baseDist);
    const deleteWallModeRef = useRef(false);
    const fixedRoomModeRef = useRef(false);
    const fixedRoomsRef = useRef<FixedRoomDrawable[]>([]);
    const pendingFixedRoomRef = useRef<FixedRoomGeometry | null>(null);
    const onFixedRoomDrawnRef = useRef<((rect: FixedRoomGeometry) => void) | undefined>(undefined);
    const fixedRoomDragRef = useRef<{
        startX: number;
        startY: number;
        currentX: number;
        currentY: number;
    } | null>(null);

    const INITIAL_ZOOM_SCALE = 10;
    const zoomTransform = useRef(d3.zoomIdentity.scale(INITIAL_ZOOM_SCALE));
    const pointsRef = useRef<Point[]>([]);
    const linesRef = useRef<Line[]>([]);
    const nextPointId = useRef(1);
    const nextLineId = useRef(1);

    // Interaction state
    const drawingRef = useRef(false);
    const draggingLineRef = useRef<{ lineId: number | null; endpoint: "a" | "b" | "new" } | null>(null);
    const tempLineIdRef = useRef<number | null>(null);
    const selectedLineIdRef = useRef<number | null>(null);
    const hoveredLineIdRef = useRef<number | null>(null);
    const hideTimeoutRef = useRef<any>(null);
    const historyRef = useRef<Line[][]>([]);
    const futureRef = useRef<Line[][]>([]);

    const movingLineRef = useRef<{
        lineId: number | null;
        startScreenX: number;
        startScreenY: number;
        origLine: Line | null;
        isMoving: boolean;
    } | null>(null);

    const deleteDragRef = useRef<{
        startX: number;
        startY: number;
        currentX: number;
        currentY: number;
        startLineId?: number;
    } | null>(null);
    const intersectedLineIdsRef = useRef<Set<number>>(new Set());

    const screenToWorld = (clientX: number, clientY: number) => {
        const node = svgRef.current;
        if (!node) return { x: 0, y: 0 };
        const rect = node.getBoundingClientRect();
        const sx = clientX - rect.left;
        const sy = clientY - rect.top;
        const t = zoomTransform.current;
        return { x: (sx - t.x) / t.k, y: (sy - t.y) / t.k };
    };

    useEffect(() => {
        drawingRef.current = drawing;
    }, [drawing]);

    useEffect(() => {
        deleteWallModeRef.current = deleteWallMode;
    }, [deleteWallMode]);

    useEffect(() => {
        fixedRoomModeRef.current = fixedRoomMode;
        selectedLineIdRef.current = null;
        renderFixedRooms();
    }, [fixedRoomMode]);

    useEffect(() => {
        fixedRoomsRef.current = fixedRooms;
        renderFixedRooms();
    }, [fixedRooms]);

    useEffect(() => {
        pendingFixedRoomRef.current = pendingFixedRoom;
        renderFixedRooms();
    }, [pendingFixedRoom]);

    useEffect(() => {
        onFixedRoomDrawnRef.current = onFixedRoomDrawn;
    }, [onFixedRoomDrawn]);

    const bumpRevision = () => setRevision((prev) => prev + 1);

    const cloneLines = (lines: Line[]) => lines.map((line) => ({ ...line }));

    const rebuildPointsFromLines = (nextLines: Line[]) => {
        const pts: Point[] = [];
        const seen = new Set<string>();
        let pid = 1;

        nextLines.forEach((line) => {
            const k1 = `${line.x1},${line.y1}`;
            const k2 = `${line.x2},${line.y2}`;
            if (!seen.has(k1)) {
                seen.add(k1);
                pts.push({ x: line.x1, y: line.y1, id: pid++ });
            }
            if (!seen.has(k2)) {
                seen.add(k2);
                pts.push({ x: line.x2, y: line.y2, id: pid++ });
            }
        });

        pointsRef.current = pts;
        nextPointId.current = pid;
        nextLineId.current = Math.max(...nextLines.map((l) => l.id), 0) + 1;
    };

    const syncHistoryState = () => {
        setCanUndo(historyRef.current.length > 0);
        setCanRedo(futureRef.current.length > 0);
    };

    const pushHistorySnapshot = () => {
        historyRef.current.push(cloneLines(linesRef.current));
        if (historyRef.current.length > 100) historyRef.current.shift();
        futureRef.current = [];
        syncHistoryState();
    };

    const renderAll = () => {
        renderPoints();
        renderLines();
        renderDimensions();
        renderDeleteButtons();
        bumpRevision();
    };

    const applyLinesState = (nextLines: Line[]) => {
        linesRef.current = cloneLines(nextLines);
        selectedLineIdRef.current = null;
        rebuildPointsFromLines(linesRef.current);
        renderAll();
    };

    useEffect(() => {
        baseDistRef.current = baseDist;
        // Redraw grid when baseDist changes
        if (svgRef.current) {
            drawGrid(getAdaptiveDist(baseDist, zoomTransform.current.k));
        }
    }, [baseDist]);

    // --- Helpers ---
    const getAdaptiveDist = (base: number, z: number) => {
        let dist = base;
        let s = dist * z;
        while (s < 4) { dist *= 2; s = dist * z; }
        while (s > 100) { dist /= 2; s = dist * z; }
        return dist;
    };

    const snapToGrid = (x: number, y: number, dist: number) => ({
        x: Math.round(x / dist) * dist,
        y: Math.round(y / dist) * dist,
    });

    const findNearestPoint = (x: number, y: number, eps = EPS) => {
        for (const p of pointsRef.current)
            if (Math.abs(p.x - x) < eps && Math.abs(p.y - y) < eps) return p;
        return null;
    };

    const getClosestEndpointToPointer = (ev: PointerEvent, line: Line) => {
        const node = svgRef.current!;
        const rect = node.getBoundingClientRect();
        const sx = ev.clientX - rect.left;
        const sy = ev.clientY - rect.top;
        const t = zoomTransform.current;
        const wx = (sx - t.x) / t.k;
        const wy = (sy - t.y) / t.k;
        const da = Math.hypot(wx - line.x1, wy - line.y1);
        const db = Math.hypot(wx - line.x2, wy - line.y2);
        if (da < db) return { endpoint: "a" as const, x: line.x1, y: line.y1, dist: da };
        return { endpoint: "b" as const, x: line.x2, y: line.y2, dist: db };
    };

    // --- Rendering ---
    const drawGrid = (baseDist: number) => {
        const svg = d3.select(svgRef.current);
        const gGrid = d3.select(gGridRef.current);
        gGrid.selectAll("*").remove();

        const width = Number(svg.attr("width"));
        const height = Number(svg.attr("height"));
        const t = zoomTransform.current;

        // Safety check if width/height are 0 or not set yet
        if (!width || !height) return;

        const inv = (sx: number, sy: number) => ({ x: (sx - t.x) / t.k, y: (sy - t.y) / t.k });
        const topLeft = inv(0, 0);
        const bottomRight = inv(width, height);

        let dist = baseDist;
        let s = dist * t.k;
        while (s < 4) { dist *= 2; s = dist * t.k; }
        while (s > 100) { dist /= 2; s = dist * t.k; }
        if (!isFinite(dist) || dist <= 0) dist = baseDist;

        visualSpacingRef.current = dist;

        const GRID_MAX_POINTS = 40000;
        let xCount = Math.ceil((bottomRight.x - topLeft.x) / dist) + 2;
        let yCount = Math.ceil((bottomRight.y - topLeft.y) / dist) + 2;
        while (xCount * yCount > GRID_MAX_POINTS) {
            dist *= 2;
            xCount = Math.ceil((bottomRight.x - topLeft.x) / dist) + 2;
            yCount = Math.ceil((bottomRight.y - topLeft.y) / dist) + 2;
        }

        const xStart = Math.floor(topLeft.x / dist) * dist;
        const xEnd = Math.ceil(bottomRight.x / dist) * dist;
        const yStart = Math.floor(topLeft.y / dist) * dist;
        const yEnd = Math.ceil(bottomRight.y / dist) * dist;

        const pts: { x: number; y: number }[] = [];
        for (const x of d3.range(xStart, xEnd + 0.5, dist))
            for (const y of d3.range(yStart, yEnd + 0.5, dist))
                pts.push({ x, y });

        const DOT_SIZE = Math.max(1, Math.min(2, 2 / t.k));
        gGrid.selectAll("rect.dot").data(pts).join("rect")
            .attr("class", "dot").attr("x", (d) => d.x - DOT_SIZE / t.k / 2)
            .attr("y", (d) => d.y - DOT_SIZE / t.k / 2)
            .attr("width", DOT_SIZE / t.k).attr("height", DOT_SIZE / t.k)
            .attr("fill", "#cfcfcf").attr("pointer-events", "none");

        setSpacing(dist);
    };

    const renderLines = () => {
        const g = d3.select(gLinesRef.current);
        const t = zoomTransform.current;
        const strokeWidth = 2 / t.k;

        const sel = g.selectAll<SVGLineElement, Line>("line.userline").data(linesRef.current, (d: any) => d.id);
        sel.exit().remove();
        const entered = sel.enter().append("line")
            .attr("class", "userline").attr("pointer-events", "stroke")
            .on("pointerdown", (e: any, d: Line) => handleLinePointerDown(e as PointerEvent, d))
            .on("dblclick", (e: any, d: Line) => handleLineDblClick(e as MouseEvent, d))
            .on("mouseenter", (e: any, d: Line) => {
                if (!drawingRef.current && !fixedRoomModeRef.current && !deleteWallModeRef.current) {
                    if (hideTimeoutRef.current) {
                        clearTimeout(hideTimeoutRef.current);
                        hideTimeoutRef.current = null;
                    }
                    hoveredLineIdRef.current = d.id;
                    renderDeleteButtons();
                }
            })
            .on("mouseleave", (e: any, d: Line) => {
                if (!drawingRef.current && !fixedRoomModeRef.current && !deleteWallModeRef.current) {
                    hideTimeoutRef.current = setTimeout(() => {
                        if (hoveredLineIdRef.current === d.id) {
                            hoveredLineIdRef.current = null;
                            renderDeleteButtons();
                        }
                    }, 100);
                }
            });

        sel.merge(entered as any)
            .attr("x1", (d) => d.x1).attr("y1", (d) => d.y1)
            .attr("x2", (d) => d.x2).attr("y2", (d) => d.y2)
            .attr("stroke", (d) => {
                if (intersectedLineIdsRef.current.has(d.id)) return "#e53e3e";
                return d.id === selectedLineIdRef.current ? "#0077ff" : "#d33";
            })
            .attr("stroke-width", (d) => {
                if (intersectedLineIdsRef.current.has(d.id)) return strokeWidth * 1.8;
                return d.id === selectedLineIdRef.current ? strokeWidth * 1.4 : strokeWidth;
            })
            .attr("stroke-dasharray", (d) => {
                if (intersectedLineIdsRef.current.has(d.id)) return `${4 / t.k} ${2 / t.k}`;
                return null;
            })
            .attr("opacity", (d) => {
                if (intersectedLineIdsRef.current.has(d.id)) return 0.5;
                return 1.0;
            })
            .attr("stroke-linecap", "round")
            .style("cursor", deleteWallModeRef.current ? "pointer" : drawingRef.current ? "crosshair" : "grab");
    };

    const renderPoints = () => {
        const g = d3.select(gPointsRef.current);
        const t = zoomTransform.current;
        const rScreen = Math.max(6, 8);
        const sel = g.selectAll<SVGCircleElement, Point>("circle.point").data(pointsRef.current, (d: any) => d.id);
        sel.exit().remove();
        sel.enter().append("circle").attr("class", "point").attr("pointer-events", "all")
            .on("pointerdown", (e: any, d: Point) => handlePointPointerDown(e as PointerEvent, d))
            .merge(sel as any)
            .attr("cx", (d) => d.x).attr("cy", (d) => d.y)
            .attr("r", rScreen / (t.k || 1))
            .attr("fill", "transparent").attr("stroke", "none")
            .style("cursor", deleteWallModeRef.current ? "default" : drawingRef.current ? "crosshair" : "default");
    };

    const renderDimensions = () => {
        const g = d3.select(gDimensionsRef.current);
        const t = zoomTransform.current;
        const fontSize = 20 / t.k;
        const offsetDist = 20 / t.k;

        // Calculate data for labels
        const labels = linesRef.current.map(line => {
            const length = Math.hypot(line.x2 - line.x1, line.y2 - line.y1);
            const text = Math.round(length).toString();

            const midX = (line.x1 + line.x2) / 2;
            const midY = (line.y1 + line.y2) / 2;
            const dx = line.x2 - line.x1;
            const dy = line.y2 - line.y1;
            let angle = Math.atan2(dy, dx) * 180 / Math.PI;

            // Normalize angle to be readable (not upside down)
            if (angle > 90 || angle < -90) {
                angle += 180;
            }

            // Offset to the left side of the drawn line (outside for clockwise walls)
            const len = Math.max(0.0001, Math.hypot(dx, dy));
            const nx = dy / len;
            const ny = -dx / len;

            const ox = nx * offsetDist;
            const oy = ny * offsetDist;

            return {
                id: line.id,
                x: midX + ox,
                y: midY + oy,
                text,
                angle: angle
            };
        });

        const sel = g.selectAll<SVGTextElement, any>("text.dim").data(labels, (d: any) => d.id);
        sel.exit().remove();

        sel.enter().append("text")
            .attr("class", "dim")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("fill", "#000")
            .style("user-select", "none")
            .style("pointer-events", "none")
            .merge(sel as any)
            .text(d => d.text)
            .attr("x", d => d.x)
            .attr("y", d => d.y)
            .attr("font-size", fontSize)
            .attr("transform", d => `rotate(${d.angle}, ${d.x}, ${d.y})`);
    };

    const renderDeleteButtons = () => {
        if (!gDeleteButtonsRef.current) return;
        const g = d3.select(gDeleteButtonsRef.current);
        const t = zoomTransform.current;

        if (drawingRef.current || fixedRoomModeRef.current || deleteWallModeRef.current) {
            g.selectAll("*").remove();
            return;
        }

        const activeIds = new Set<number>();
        if (selectedLineIdRef.current !== null) {
            activeIds.add(selectedLineIdRef.current);
        }
        if (hoveredLineIdRef.current !== null) {
            activeIds.add(hoveredLineIdRef.current);
        }

        const linesToShow = linesRef.current.filter((l) => activeIds.has(l.id));

        const buttonRadius = 11; // px on screen
        const rWorld = buttonRadius / t.k;

        const sel = g.selectAll<SVGGElement, Line>("g.delete-btn-grp").data(linesToShow, (d: any) => d.id);
        sel.exit().remove();

        const entered = sel.enter().append("g")
            .attr("class", "delete-btn-grp")
            .style("cursor", "pointer")
            .on("pointerdown", (e: any) => {
                e.stopPropagation();
            })
            .on("mouseenter", function() {
                if (hideTimeoutRef.current) {
                    clearTimeout(hideTimeoutRef.current);
                    hideTimeoutRef.current = null;
                }
                d3.select(this).select("circle.bg-circle").attr("fill", "#c53030");
            })
            .on("mouseleave", function() {
                d3.select(this).select("circle.bg-circle").attr("fill", "#e53e3e");
                hoveredLineIdRef.current = null;
                renderDeleteButtons();
            })
            .on("click", (e: any, d: Line) => {
                e.stopPropagation();
                pushHistorySnapshot();
                const nextLines = linesRef.current.filter((l) => l.id !== d.id);
                applyLinesState(nextLines);
                hoveredLineIdRef.current = null;
                renderDeleteButtons();
            });

        entered.append("circle")
            .attr("class", "hit-circle")
            .attr("fill", "transparent")
            .attr("stroke", "none")
            .style("pointer-events", "all");

        entered.append("circle")
            .attr("class", "bg-circle")
            .attr("fill", "#e53e3e")
            .attr("stroke", "#ffffff")
            .style("transition", "fill 0.1s ease");

        entered.append("text")
            .attr("class", "delete-x")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "central")
            .attr("fill", "#ffffff")
            .attr("font-weight", "bold")
            .text("×");

        const merged = sel.merge(entered as any);

        merged.select("circle.hit-circle")
            .attr("cx", (d) => (d.x1 + d.x2) / 2)
            .attr("cy", (d) => (d.y1 + d.y2) / 2)
            .attr("r", (buttonRadius + 8) / t.k);

        merged.select("circle.bg-circle")
            .attr("cx", (d) => (d.x1 + d.x2) / 2)
            .attr("cy", (d) => (d.y1 + d.y2) / 2)
            .attr("r", rWorld)
            .attr("stroke-width", 1.5 / t.k);

        merged.select("text.delete-x")
            .attr("x", (d) => (d.x1 + d.x2) / 2)
            .attr("y", (d) => (d.y1 + d.y2) / 2)
            .attr("font-size", `${14 / t.k}px`);
    };

    const renderDeleteDrag = () => {
        if (!gDeleteButtonsRef.current) return;
        const g = d3.select(gDeleteButtonsRef.current);
        const t = zoomTransform.current;

        const drag = deleteDragRef.current;
        if (!drag) {
            g.selectAll(".delete-drag-line").remove();
            return;
        }

        // Draw selection line
        g.selectAll(".delete-drag-line")
            .data([drag])
            .join("line")
            .attr("class", "delete-drag-line")
            .attr("x1", d => d.startX)
            .attr("y1", d => d.startY)
            .attr("x2", d => d.currentX)
            .attr("y2", d => d.currentY)
            .attr("stroke", "#ff4d4d")
            .attr("stroke-width", 2.5 / t.k)
            .attr("stroke-dasharray", `${6 / t.k} ${4 / t.k}`)
            .attr("pointer-events", "none");
    };

    const renderFixedRooms = () => {
        if (!gFixedRoomsRef.current) return;

        type RenderRect = FixedRoomGeometry & {
            key: string;
            name?: string;
            isPreview?: boolean;
            isPending?: boolean;
        };

        const committed: RenderRect[] = fixedRoomsRef.current.map((room) => ({
            key: room.id,
            x: room.x,
            y: room.y,
            width: room.width,
            height: room.height,
            name: room.name,
            isPreview: false,
            isPending: false,
        }));

        const pending: RenderRect[] = pendingFixedRoomRef.current
            ? [{
                key: "pending-fixed-room",
                ...pendingFixedRoomRef.current,
                name: "Pending",
                isPreview: false,
                isPending: true,
            }]
            : [];

        const dragging = fixedRoomDragRef.current;
        const preview: RenderRect[] = dragging
            ? [{
                key: "preview-fixed-room",
                ...normalizeRect(dragging.startX, dragging.startY, dragging.currentX, dragging.currentY),
                name: "Drawing...",
                isPreview: true,
                isPending: false,
            }]
            : [];

        const rects = [...committed, ...pending, ...preview]
            .filter((r) => r.width > EPS && r.height > EPS);

        const g = d3.select(gFixedRoomsRef.current);
        const t = zoomTransform.current;
        const strokeWidth = Math.max(1.4 / (t.k || 1), 0.3);

        const rectSel = g
            .selectAll<SVGRectElement, RenderRect>("rect.fixed-room")
            .data(rects, (d: any) => d.key);

        rectSel.exit().remove();

        rectSel
            .enter()
            .append("rect")
            .attr("class", "fixed-room")
            .attr("pointer-events", "none")
            .merge(rectSel as any)
            .attr("x", (d) => d.x)
            .attr("y", (d) => d.y)
            .attr("width", (d) => d.width)
            .attr("height", (d) => d.height)
            .attr("fill", (d) => {
                if (d.isPreview) return "rgba(255, 154, 86, 0.18)";
                if (d.isPending) return "rgba(255, 186, 122, 0.22)";
                return "rgba(28, 76, 130, 0.18)";
            })
            .attr("stroke", (d) => (d.isPreview || d.isPending ? "#FF9A56" : "#FF9A56"))
            .attr("stroke-dasharray", (d) => (d.isPreview || d.isPending ? `${4 / (t.k || 1)} ${3 / (t.k || 1)}` : null))
            .attr("stroke-width", strokeWidth)
            .attr("rx", 2 / (t.k || 1))
            .attr("ry", 2 / (t.k || 1));

        const labelSel = g
            .selectAll<SVGTextElement, RenderRect>("text.fixed-room-label")
            .data(rects, (d: any) => d.key);

        labelSel.exit().remove();

        labelSel
            .enter()
            .append("text")
            .attr("class", "fixed-room-label")
            .attr("pointer-events", "none")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .merge(labelSel as any)
            .text((d) => d.name || "Fixed Room")
            .attr("x", (d) => d.x + d.width / 2)
            .attr("y", (d) => d.y + d.height / 2)
            .attr("fill", "#1a1a1a")
            .attr("font-size", Math.max(14 / (t.k || 1), 1.5));
    };


    // --- Handlers ---
    function handlePointPointerDown(event: PointerEvent, d: Point) {
        if (fixedRoomModeRef.current) return;
        event.stopPropagation();
        if (deleteWallModeRef.current) {
            const w = { x: d.x, y: d.y };
            deleteDragRef.current = {
                startX: w.x,
                startY: w.y,
                currentX: w.x,
                currentY: w.y
            };
            intersectedLineIdsRef.current.clear();
            try { (event.target as Element).setPointerCapture((event as any).pointerId); } catch { }
            renderDeleteDrag();
            renderLines();
            return;
        }
        if (!drawingRef.current) return;
        pushHistorySnapshot();
        const start = d;
        const newLine: Line = { x1: start.x, y1: start.y, x2: start.x, y2: start.y, id: nextLineId.current++ };
        linesRef.current = [...linesRef.current, newLine];
        tempLineIdRef.current = newLine.id;
        draggingLineRef.current = { lineId: newLine.id, endpoint: "new" };
        renderLines();
        renderDimensions();
        try { (event.target as Element).setPointerCapture((event as any).pointerId); } catch { }
    }

    function handleLinePointerDown(event: PointerEvent, d: Line) {
        if (fixedRoomModeRef.current) return;
        event.stopPropagation();

        if (event.altKey) {
            pushHistorySnapshot();
            const nextLines = linesRef.current.filter((line) => line.id !== d.id);
            applyLinesState(nextLines);
            if (hoveredLineIdRef.current === d.id) {
                hoveredLineIdRef.current = null;
            }
            renderDeleteButtons();
            return;
        }

        if (deleteWallModeRef.current && !drawingRef.current) {
            const w = screenToWorld(event.clientX, event.clientY);
            deleteDragRef.current = {
                startX: w.x,
                startY: w.y,
                currentX: w.x,
                currentY: w.y,
                startLineId: d.id
            };
            intersectedLineIdsRef.current = new Set([d.id]);
            try { (event.target as Element).setPointerCapture((event as any).pointerId); } catch { }
            renderDeleteDrag();
            renderLines();
            return;
        }

        const node = svgRef.current!;
        const rect = node.getBoundingClientRect();
        const sx = event.clientX - rect.left;
        const sy = event.clientY - rect.top;

        if (drawingRef.current) {
            pushHistorySnapshot();
            const closest = getClosestEndpointToPointer(event, d);
            draggingLineRef.current = { lineId: d.id, endpoint: closest!.endpoint };
            tempLineIdRef.current = d.id;
            let pt = findNearestPoint(closest!.x, closest!.y, EPS);
            if (!pt) {
                pt = { x: closest!.x, y: closest!.y, id: nextPointId.current++ };
                pointsRef.current = [...pointsRef.current, pt];
                renderPoints();
            }
            try { (event.target as Element).setPointerCapture((event as any).pointerId); } catch { }
            return;
        }
        selectedLineIdRef.current = d.id === selectedLineIdRef.current ? null : d.id;
        renderLines();
        renderDeleteButtons();
        // renderDimensions(); // No change in dims just selection
        pushHistorySnapshot();
        movingLineRef.current = { lineId: d.id, startScreenX: sx, startScreenY: sy, origLine: { ...d }, isMoving: false };
        try { (event.target as Element).setPointerCapture((event as any).pointerId); } catch { }
    }

    function handleLineDblClick(event: MouseEvent, d: Line) {
        if (fixedRoomModeRef.current || drawingRef.current) return;
        event.stopPropagation();
        pushHistorySnapshot();
        const nextLines = linesRef.current.filter((line) => line.id !== d.id);
        applyLinesState(nextLines);
        if (hoveredLineIdRef.current === d.id) {
            hoveredLineIdRef.current = null;
        }
        renderDeleteButtons();
    }

    const zoomBehaviorRef = useRef<any>(null);

    // --- Main Effect ---
    useEffect(() => {
        const svg = d3.select(svgRef.current)!;
        const gGrid = d3.select(gGridRef.current)!;
        const gPoints = d3.select(gPointsRef.current)!;
        const gLines = d3.select(gLinesRef.current)!;
        const gFixedRooms = d3.select(gFixedRoomsRef.current)!;
        const gDimensions = d3.select(gDimensionsRef.current)!;
        const gDeleteButtons = d3.select(gDeleteButtonsRef.current)!;

        const resize = () => {
            svg.attr("width", window.innerWidth).attr("height", window.innerHeight);
            drawGrid(getAdaptiveDist(baseDistRef.current, zoomTransform.current.k));
            renderPoints();
            renderLines();
            renderFixedRooms();
            renderDimensions();
            renderDeleteButtons();
        };
        window.addEventListener("resize", resize);
        resize();


        const customFilter = (event: any) => {
            if (
                drawingRef.current ||
                deleteWallModeRef.current ||
                fixedRoomModeRef.current ||
                draggingLineRef.current ||
                fixedRoomDragRef.current ||
                (movingLineRef.current && movingLineRef.current.lineId != null)
            ) return false;
            if (!event) return false;
            if (event.button === 2 || event.ctrlKey || event.altKey || event.metaKey) return false;
            return true;
        };

        const zoomed = (event: any) => {
            zoomTransform.current = event.transform;
            gGrid.attr("transform", event.transform);
            gPoints.attr("transform", event.transform);
            gLines.attr("transform", event.transform);
            gFixedRooms.attr("transform", event.transform);
            gDimensions.attr("transform", event.transform);
            gDeleteButtons.attr("transform", event.transform);
            setZoom(event.transform.k);
            drawGrid(getAdaptiveDist(baseDistRef.current, event.transform.k));
            renderPoints();
            renderLines();
            renderFixedRooms();
            renderDimensions();
            renderDeleteButtons();
        };

        const zoomBehavior = d3.zoom<SVGSVGElement, unknown>().filter(customFilter).scaleExtent([0.05, 20]).on("zoom", zoomed);
        zoomBehaviorRef.current = zoomBehavior;
        svg.call(zoomBehavior as any);

        // Apply initial zoom centered on the canvas
        const initT = zoomTransform.current;
        const cx = window.innerWidth / 2;
        const cy = window.innerHeight / 2;
        const initialTransform = d3.zoomIdentity
            .translate(cx - cx * initT.k, cy - cy * initT.k)
            .scale(initT.k);
        svg.call(zoomBehavior.transform as any, initialTransform);

        const node = svg.node() as SVGSVGElement;

        const isPrimaryPointer = (ev: PointerEvent) => ev.pointerType === "mouse" ? ev.button === 0 : true;

        const onBackgroundPointerDown = (ev: PointerEvent) => {
            if (fixedRoomModeRef.current && isPrimaryPointer(ev) && !deleteWallModeRef.current) {
                const w = screenToWorld(ev.clientX, ev.clientY);
                const s = snapToGrid(w.x, w.y, visualSpacingRef.current);
                fixedRoomDragRef.current = {
                    startX: s.x,
                    startY: s.y,
                    currentX: s.x,
                    currentY: s.y,
                };
                renderFixedRooms();
                try {
                    const pointerId = (ev as any).pointerId;
                    if (pointerId !== undefined && pointerId !== null && node.setPointerCapture) {
                        node.setPointerCapture(pointerId);
                    }
                } catch { }
                return;
            }

            if (deleteWallModeRef.current && isPrimaryPointer(ev)) {
                const w = screenToWorld(ev.clientX, ev.clientY);
                deleteDragRef.current = {
                    startX: w.x,
                    startY: w.y,
                    currentX: w.x,
                    currentY: w.y
                };
                intersectedLineIdsRef.current.clear();
                try {
                    const pointerId = (ev as any).pointerId;
                    if (pointerId !== undefined && pointerId !== null && node.setPointerCapture) {
                        node.setPointerCapture(pointerId);
                    }
                } catch { }
                renderDeleteDrag();
                renderLines();
                return;
            }

            if (!drawingRef.current || !isPrimaryPointer(ev)) return;
            pushHistorySnapshot();
            const w = screenToWorld(ev.clientX, ev.clientY);
            const s = snapToGrid(w.x, w.y, visualSpacingRef.current);
            let pt = findNearestPoint(s.x, s.y, EPS);
            if (!pt) {
                pt = { x: s.x, y: s.y, id: nextPointId.current++ };
                pointsRef.current = [...pointsRef.current, pt];
                renderPoints();
            }
            const newLine: Line = { x1: pt.x, y1: pt.y, x2: pt.x, y2: pt.y, id: nextLineId.current++ };
            linesRef.current = [...linesRef.current, newLine];
            tempLineIdRef.current = newLine.id;
            draggingLineRef.current = { lineId: newLine.id, endpoint: "new" };
            renderLines();
            renderDimensions();
            try { (ev.target as Element).setPointerCapture((ev as any).pointerId); } catch { }
        };

        const onPointerMove = (ev: PointerEvent) => {
            if (deleteDragRef.current) {
                const drag = deleteDragRef.current;
                const w = screenToWorld(ev.clientX, ev.clientY);
                drag.currentX = w.x;
                drag.currentY = w.y;

                const newIntersected = new Set<number>();
                if (drag.startLineId !== undefined) {
                    newIntersected.add(drag.startLineId);
                }
                linesRef.current.forEach((line) => {
                    if (lineSegmentsIntersect(
                        drag.startX, drag.startY, drag.currentX, drag.currentY,
                        line.x1, line.y1, line.x2, line.y2
                    )) {
                        newIntersected.add(line.id);
                    }
                });
                intersectedLineIdsRef.current = newIntersected;

                renderDeleteDrag();
                renderLines();
                return;
            }

            if (fixedRoomDragRef.current) {
                const drag = fixedRoomDragRef.current;
                const w = screenToWorld(ev.clientX, ev.clientY);
                const s = snapToGrid(w.x, w.y, visualSpacingRef.current);
                drag.currentX = s.x;
                drag.currentY = s.y;
                renderFixedRooms();
                return;
            }

            if (draggingLineRef.current) {
                const drag = draggingLineRef.current;
                const lnId = drag.lineId;
                if (lnId == null) return;
                const w = screenToWorld(ev.clientX, ev.clientY);
                let s = snapToGrid(w.x, w.y, visualSpacingRef.current);
                const idx = linesRef.current.findIndex((l) => l.id === lnId);
                if (idx < 0) return;
                const existing = linesRef.current[idx];

                let anchor = { x: existing.x1, y: existing.y1 }; // default for drag.endpoint === "b" or "new"
                if (drag.endpoint === "a") anchor = { x: existing.x2, y: existing.y2 };

                const dx = Math.abs(s.x - anchor.x);
                const dy = Math.abs(s.y - anchor.y);
                if (dx >= dy) s.y = anchor.y; // horizontal
                else s.x = anchor.x; // vertical

                if (drag.endpoint === "a") linesRef.current[idx] = { ...existing, x1: s.x, y1: s.y };
                else if (drag.endpoint === "b") linesRef.current[idx] = { ...existing, x2: s.x, y2: s.y };
                else linesRef.current[idx] = { ...existing, x2: s.x, y2: s.y };

                const ptExists = findNearestPoint(s.x, s.y, EPS);
                if (!ptExists) {
                    const id = nextPointId.current++;
                    pointsRef.current = [...pointsRef.current, { x: s.x, y: s.y, id }];
                    renderPoints();
                }
                renderLines();
                renderDimensions();
                return;
            }

            if (movingLineRef.current && movingLineRef.current.lineId != null) {
                const mv = movingLineRef.current;
                const idx = linesRef.current.findIndex((l) => l.id === mv.lineId);
                if (idx < 0) return;
                const rect = node.getBoundingClientRect();
                const sx = ev.clientX - rect.left;
                const sy = ev.clientY - rect.top;
                const t = zoomTransform.current;
                const dxWorld = (sx - mv.startScreenX) / t.k;
                const dyWorld = (sy - mv.startScreenY) / t.k;
                const orig = mv.origLine!;
                const newLine = { ...orig, x1: orig.x1 + dxWorld, y1: orig.y1 + dyWorld, x2: orig.x2 + dxWorld, y2: orig.y2 + dyWorld };
                linesRef.current[idx] = newLine;
                renderLines();
                renderDimensions();
            }
        };

        const onPointerUp = (ev: PointerEvent) => {
            if (deleteDragRef.current) {
                deleteDragRef.current = null;
                try { (ev.target as Element).releasePointerCapture((ev as any).pointerId); } catch { }

                const toDelete = Array.from(intersectedLineIdsRef.current);
                intersectedLineIdsRef.current.clear();

                if (toDelete.length > 0) {
                    pushHistorySnapshot();
                    const nextLines = linesRef.current.filter((line) => !toDelete.includes(line.id));
                    applyLinesState(nextLines);
                }

                renderDeleteDrag();
                renderLines();
                renderPoints();
                renderDimensions();
                return;
            }

            if (fixedRoomDragRef.current) {
                const drag = fixedRoomDragRef.current;
                fixedRoomDragRef.current = null;
                try {
                    const pointerId = (ev as any).pointerId;
                    if (pointerId !== undefined && pointerId !== null && node.releasePointerCapture) {
                        node.releasePointerCapture(pointerId);
                    }
                } catch { }

                const rect = normalizeRect(drag.startX, drag.startY, drag.currentX, drag.currentY);
                renderFixedRooms();

                if (rect.width >= visualSpacingRef.current && rect.height >= visualSpacingRef.current) {
                    onFixedRoomDrawnRef.current?.(rect);
                }
                return;
            }

            if (draggingLineRef.current) {
                const drag = draggingLineRef.current;
                const lnId = drag.lineId;
                draggingLineRef.current = null;
                tempLineIdRef.current = null;
                try { (ev.target as Element).releasePointerCapture((ev as any).pointerId); } catch { }

                if (lnId == null) return;
                const idx = linesRef.current.findIndex((l) => l.id === lnId);
                if (idx >= 0) {
                    const l = linesRef.current[idx];
                    if (Math.abs(l.x1 - l.x2) < EPS && Math.abs(l.y1 - l.y2) < EPS) {
                        linesRef.current.splice(idx, 1);
                    } else {
                        if (!findNearestPoint(l.x1, l.y1, EPS)) pointsRef.current.push({ x: l.x1, y: l.y1, id: nextPointId.current++ });
                        if (!findNearestPoint(l.x2, l.y2, EPS)) pointsRef.current.push({ x: l.x2, y: l.y2, id: nextPointId.current++ });
                    }
                    renderPoints();
                    renderLines();
                    renderDimensions();
                    bumpRevision();
                }
            }
            if (movingLineRef.current && movingLineRef.current.lineId != null) {
                const mv = movingLineRef.current;
                const lnId = mv.lineId;
                movingLineRef.current = null;
                try { (ev.target as Element).releasePointerCapture((ev as any).pointerId); } catch { }
                if (lnId == null) return;
                const idx = linesRef.current.findIndex((l) => l.id === lnId);
                if (idx >= 0) {
                    // Snap final position
                    let l = linesRef.current[idx];
                    const sp1 = snapToGrid(l.x1, l.y1, visualSpacingRef.current);
                    const sp2 = snapToGrid(l.x2, l.y2, visualSpacingRef.current);
                    linesRef.current[idx] = { ...l, x1: sp1.x, y1: sp1.y, x2: sp2.x, y2: sp2.y };
                    l = linesRef.current[idx];
                    if (!findNearestPoint(l.x1, l.y1, EPS)) pointsRef.current.push({ x: l.x1, y: l.y1, id: nextPointId.current++ });
                    if (!findNearestPoint(l.x2, l.y2, EPS)) pointsRef.current.push({ x: l.x2, y: l.y2, id: nextPointId.current++ });
                    renderPoints();
                    renderLines();
                    renderDimensions();
                    bumpRevision();
                }
            }
        };

        const onBackgroundClick = (ev: MouseEvent) => {
            if (fixedRoomModeRef.current) return;
            if (deleteWallModeRef.current) return;
            if (!drawingRef.current && node && ev.target === node) {
                selectedLineIdRef.current = null;
                renderLines();
                renderDeleteButtons();
            }
        };

        const onKeyDown = (ev: KeyboardEvent) => {
            if (ev.key === "Delete" || ev.key === "Backspace") {
                const sel = selectedLineIdRef.current;
                if (sel != null) {
                    pushHistorySnapshot();
                    const nextLines = linesRef.current.filter((l) => l.id !== sel);
                    applyLinesState(nextLines);
                    if (hoveredLineIdRef.current === sel) {
                        hoveredLineIdRef.current = null;
                    }
                    renderDeleteButtons();
                }
            } else if (ev.key === "Escape") {
                if (deleteDragRef.current) {
                    deleteDragRef.current = null;
                    intersectedLineIdsRef.current.clear();
                    renderDeleteDrag();
                    renderLines();
                } else if (draggingLineRef.current && tempLineIdRef.current !== null) {
                    // Cancel drawing the active line
                    const tempId = tempLineIdRef.current;
                    linesRef.current = linesRef.current.filter((l) => l.id !== tempId);
                    draggingLineRef.current = null;
                    tempLineIdRef.current = null;
                    rebuildPointsFromLines(linesRef.current);
                    renderAll();
                } else if (selectedLineIdRef.current !== null) {
                    // Deselect selected line
                    selectedLineIdRef.current = null;
                    renderLines();
                    renderDeleteButtons();
                }
            }
        };

        node.addEventListener("pointerdown", onBackgroundPointerDown as any);
        node.addEventListener("pointermove", onPointerMove as any);
        node.addEventListener("pointerup", onPointerUp as any);
        node.addEventListener("pointercancel", onPointerUp as any);
        node.addEventListener("click", onBackgroundClick);
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("resize", resize);
            node.removeEventListener("pointerdown", onBackgroundPointerDown as any);
            node.removeEventListener("pointermove", onPointerMove as any);
            node.removeEventListener("pointerup", onPointerUp as any);
            node.removeEventListener("pointercancel", onPointerUp as any);
            node.removeEventListener("click", onBackgroundClick);
            window.removeEventListener("keydown", onKeyDown);
            svg.on(".zoom", null);
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, []); // End effect

    const handleZoom = (factor: number) => {
        const svg = d3.select(svgRef.current);
        const t = zoomTransform.current;
        const newK = Math.max(0.05, Math.min(20, t.k * factor));
        const width = window.innerWidth / 2;
        const height = window.innerHeight / 2;
        const newTransform = d3.zoomIdentity.translate(width - (width - t.x) * (newK / t.k), height - (height - t.y) * (newK / t.k)).scale(newK);

        if (zoomBehaviorRef.current) {
            svg.transition().duration(150).call(zoomBehaviorRef.current.transform, newTransform);
        }
    };

    const deleteSelectedLine = () => {
        const sel = selectedLineIdRef.current;
        if (sel != null) {
            pushHistorySnapshot();
            const nextLines = linesRef.current.filter(l => l.id !== sel);
            applyLinesState(nextLines);
        }
    }

    const clearAll = () => {
        if (linesRef.current.length > 0) {
            pushHistorySnapshot();
        }
        pointsRef.current = [];
        linesRef.current = [];
        fixedRoomDragRef.current = null;
        deleteDragRef.current = null;
        intersectedLineIdsRef.current.clear();
        selectedLineIdRef.current = null;
        nextPointId.current = 1;
        nextLineId.current = 1;
        const gL = d3.select(gLinesRef.current);
        gL.selectAll("*").remove();
        const gP = d3.select(gPointsRef.current);
        gP.selectAll("*").remove();
        const gD = d3.select(gDimensionsRef.current);
        gD.selectAll("*").remove();
        const gDel = d3.select(gDeleteButtonsRef.current);
        gDel.selectAll("*").remove();
        setDeleteWallMode(false);
        setDrawing(true);
        renderFixedRooms();
        bumpRevision();
    }

    const undo = () => {
        if (historyRef.current.length === 0) return;
        futureRef.current.push(cloneLines(linesRef.current));
        const previous = historyRef.current.pop() || [];
        applyLinesState(previous);
        syncHistoryState();
    };

    const redo = () => {
        if (futureRef.current.length === 0) return;
        historyRef.current.push(cloneLines(linesRef.current));
        const next = futureRef.current.pop() || [];
        applyLinesState(next);
        syncHistoryState();
    };

    const restoreLines = (savedLines: Line[]) => {
        if (!savedLines || savedLines.length === 0) return;
        historyRef.current = [];
        futureRef.current = [];
        syncHistoryState();
        // Trigger D3 re-render
        const gL = d3.select(gLinesRef.current);
        gL.selectAll("*").remove();
        const gP = d3.select(gPointsRef.current);
        gP.selectAll("*").remove();
        const gD = d3.select(gDimensionsRef.current);
        gD.selectAll("*").remove();
        const gDel = d3.select(gDeleteButtonsRef.current);
        gDel.selectAll("*").remove();
        applyLinesState(savedLines);
    };

    const setDrawingMode = (enabled: boolean | ((prev: boolean) => boolean)) => {
        const nextDrawing = typeof enabled === "function" ? enabled(drawingRef.current) : enabled;
        setDrawing(nextDrawing);
        if (nextDrawing) {
            setDeleteWallMode(false);
        }
        selectedLineIdRef.current = null;
        renderLines();
        renderPoints();
        renderDeleteButtons();
    };

    const setDeleteMode = (enabled: boolean) => {
        setDeleteWallMode(enabled);
        if (enabled) {
            setDrawing(false);
        }
        deleteDragRef.current = null;
        intersectedLineIdsRef.current.clear();
        selectedLineIdRef.current = null;
        renderDeleteDrag();
        renderLines();
        renderPoints();
        renderDeleteButtons();
    };

    const restoreZoom = (t: { x: number; y: number; k: number }) => {
        if (!zoomBehaviorRef.current || !svgRef.current) return;
        const svg = d3.select(svgRef.current);
        const transform = d3.zoomIdentity.translate(t.x, t.y).scale(t.k);
        svg.call(zoomBehaviorRef.current.transform, transform);
    };

    return {
        refs: { svgRef, gGridRef, gLinesRef, gFixedRoomsRef, gPointsRef, gDimensionsRef, gDeleteButtonsRef },
        state: { zoom, drawing, spacing, linesRef, zoomTransform, deleteWallMode, revision, canUndo, canRedo }, // exposed zoomTransform
        actions: { setDrawing: setDrawingMode, handleZoom, deleteSelectedLine, clearAll, restoreLines, restoreZoom, undo, redo, setDeleteMode }
    };
};
